import React, { useCallback, useRef, useState } from 'react';
import { Magnet, Grid3x3, BrickWall, Square, Paintbrush, DoorOpen, PanelTop, TrendingUp, BoxSelect, Camera, Video, Landmark, Warehouse, Box as BoxIcon, Trees } from 'lucide-react';
import { useWorldStore, PrimitiveShape, LightType, LightItem, OnlinePlayer, BuildTool } from '../stores/worldStore';
import { broadcastObjectAdd, broadcastObjectDelete, broadcastObjectUnlock } from '../services/worldBroadcast';
import { redoWorld, undoWorld } from '../services/worldUndo';
import { usePanelOpacity } from './PanelOpacityToggle';
import { openPlayerCard } from './safety/openSafety';
import { baseMime, pickRecordingMime, type FinishedRecording } from '../services/recordingShare';
import RecordingSavedDialog from './recording/RecordingSavedDialog';

// Snap sizes cycled by the toolbar chip next to the magnet
const SNAP_SIZES = [0.25, 0.5, 1];

// Curated paint-mode palette (neutral architectural tones + brand accents)
const PAINT_SWATCHES = [
  '#ffffff', '#d9d2c5', '#b8b0a4', '#8a8378', '#5b5750',
  '#2b3a4a', '#00c2b3', '#4f8cff', '#f59e0b', '#ef4444',
];

interface BuilderToolbarProps {
  worldId: string;
}

const SHAPES: { label: string; value: PrimitiveShape }[] = [
  { label: 'Box', value: 'box' },
  { label: 'Sphere', value: 'sphere' },
  { label: 'Cylinder', value: 'cylinder' },
  { label: 'Cone', value: 'cone' },
  { label: 'Plane', value: 'plane' },
  { label: 'Torus', value: 'torus' },
  { label: 'Text', value: 'text' },
];

const LIGHTS: { label: string; value: LightType }[] = [
  { label: 'Spotlight', value: 'spotlight' },
  { label: 'Point Light', value: 'pointlight' },
  { label: 'Area Light', value: 'arealight' },
];

const TOOLS = [
  { label: 'Select', value: 'select' as const, icon: '↖' },
  { label: 'Move', value: 'move' as const, icon: '✥' },
  { label: 'Rotate', value: 'rotate' as const, icon: '↻' },
  { label: 'Scale', value: 'scale' as const, icon: '⇲' },
  { label: 'Delete Selected', value: 'delete' as const, icon: '✕' },
];

// --- Online players section ---
const nameColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 60%, 50%)`;
};
const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const BuilderToolbar: React.FC<BuilderToolbarProps> = ({ worldId }) => {
  const store = useWorldStore();
  const {
    activeTool, setActiveTool,
    snapSettings, setSnapSettings,
    selectedObjectId, setSelectedObjectId,
    setIsEditingEnvironment,
    canBuild,
    buildTool, setBuildTool,
    topDownView, setTopDownView,
    paintColor, setPaintColor,
  } = store;

  // Build Mode v2 tools are mutually exclusive — clicking the active one disarms it.
  const toggleBuildTool = useCallback((tool: BuildTool) => {
    setBuildTool(buildTool === tool ? 'none' : tool);
  }, [buildTool, setBuildTool]);

  const cycleSnapSize = useCallback(() => {
    const i = SNAP_SIZES.indexOf(snapSettings.gridSize);
    setSnapSettings({ gridSize: SNAP_SIZES[(i + 1) % SNAP_SIZES.length] });
  }, [snapSettings.gridSize, setSnapSettings]);
  const onlinePlayers: OnlinePlayer[] = useWorldStore((s) => s.onlinePlayers);
  const { solid: panelsSolid, toggle: togglePanelOpacity } = usePanelOpacity();

  const [showShapes, setShowShapes] = React.useState(false);
  const [showLights, setShowLights] = React.useState(false);
  const [showPlayers, setShowPlayers] = React.useState(false);
  const [tooltip, setTooltip] = React.useState<string | null>(null);

  // Capture: screenshot + video recording of the build canvas (same mechanism
  // as the viewer HUD — canvas.captureStream → MediaRecorder → webm download).
  const [recording, setRecording] = useState(false);
  const [finished, setFinished] = useState<FinishedRecording | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const getCanvas = () => document.querySelector('canvas') as HTMLCanvasElement | null;

  const takeScreenshot = useCallback(() => {
    const c = getCanvas();
    if (!c) return;
    const a = document.createElement('a');
    a.href = c.toDataURL('image/png');
    a.download = `gathrxr-build-${Date.now()}.png`;
    a.click();
  }, []);

  const toggleRecord = useCallback(() => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      setRecording(false);
      return;
    }
    const c = getCanvas();
    if (!c) return;
    try {
      const stream = (c as any).captureStream(30);
      const mimeType = pickRecordingMime();
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const startedAt = performance.now();
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const mime = baseMime(rec.mimeType || mimeType);
        const blob = new Blob(chunksRef.current, { type: mime });
        setFinished({ blob, mime, durationS: (performance.now() - startedAt) / 1000, worldId, worldName: useWorldStore.getState().worldName || undefined });
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (err) {
      console.error('[Toolbar] Recording failed:', err);
    }
  }, [recording, worldId]);

  // Build Mode v2: "Add Shape" arms the ghost preview instead of dropping the
  // primitive at the origin. Click on the ground to stamp it, R to rotate 90°,
  // Esc / right-click to stop. (The Objects ▸ Primitives menu in the top bar
  // still does the classic instant-add.)
  const startPlaceShape = useCallback((shape: PrimitiveShape) => {
    const s = useWorldStore.getState();
    s.setPlacementSpec({ kind: 'primitive', shape });
    s.setPlacementRotY(0);
    s.setBuildTool('place');
    setShowShapes(false);
  }, []);

  const addLight = useCallback((type: LightType) => {
    const id = `light-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
    const names: Record<LightType, string> = { spotlight: 'Spotlight', pointlight: 'Point Light', arealight: 'Area Light' };
    const item: LightItem = {
      type, name: names[type], position: [0, 3, 0], rotation: [-Math.PI / 4, 0, 0],
      color: '#ffffff', intensity: 1, distance: 10, angle: Math.PI / 6,
      penumbra: 0.5, castShadow: true, visible: true, isLocked: false, width: 4, height: 4,
    };
    store.addLight(id, item);
    broadcastObjectAdd(worldId, 'light', id, item);
    setSelectedObjectId(id);
    setIsEditingEnvironment(true);
    setShowLights(false);
  }, [worldId, store, setSelectedObjectId, setIsEditingEnvironment]);

  const handleDelete = useCallback(() => {
    if (!selectedObjectId) return;
    if (selectedObjectId.startsWith('prim-')) {
      store.deletePrimitive(selectedObjectId);
      broadcastObjectDelete(worldId, 'primitive', selectedObjectId);
    } else if (selectedObjectId.startsWith('light-')) {
      store.deleteLight(selectedObjectId);
      broadcastObjectDelete(worldId, 'light', selectedObjectId);
    }
    broadcastObjectUnlock(worldId, selectedObjectId);
    setSelectedObjectId(null);
  }, [worldId, selectedObjectId, store, setSelectedObjectId]);

  const handleDuplicate = useCallback(() => {
    if (!selectedObjectId) return;
    if (selectedObjectId.startsWith('prim-')) {
      const src = store.primitives[selectedObjectId];
      if (!src) return;
      const newId = `prim-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
      const dupItem = { ...src, position: [src.position[0] + 1.5, src.position[1], src.position[2]] as [number, number, number], name: `${src.name} Copy` };
      store.addPrimitive(newId, dupItem);
      broadcastObjectAdd(worldId, 'primitive', newId, dupItem);
      setSelectedObjectId(newId);
    } else if (selectedObjectId.startsWith('light-')) {
      const src = store.lights[selectedObjectId];
      if (!src) return;
      const newId = `light-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
      const dupItem = { ...src, position: [src.position[0] + 1.5, src.position[1], src.position[2]] as [number, number, number], name: `${src.name} Copy` };
      store.addLight(newId, dupItem);
      broadcastObjectAdd(worldId, 'light', newId, dupItem);
      setSelectedObjectId(newId);
    }
  }, [worldId, selectedObjectId, store, setSelectedObjectId]);

  const closeDropdowns = () => { setShowShapes(false); setShowLights(false); setShowPlayers(false); };

  // Show bar even if not builder (for online players), but hide build tools
  const showBuildTools = canBuild;

  return (
    <div
      style={barStyle}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Tooltip */}
      {tooltip && (
        <div style={tooltipStyle}>{tooltip}</div>
      )}

      {/* Online players */}
      <div style={{ position: 'relative' }}>
        {showPlayers && onlinePlayers.length > 0 && (
          <div style={{ ...dropdownStyle, minWidth: 160 }}>
            {onlinePlayers.map((p) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', cursor: 'pointer' }}
                title={`${p.userName}: mute, block or report`}
                onClick={() => { setShowPlayers(false); openPlayerCard({ userId: p.userId || null, key: p.id, name: p.userName }); }}>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%', background: nameColor(p.userName),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 9, fontWeight: 700, color: '#fff', flexShrink: 0,
                }}>{getInitials(p.userName)}</div>
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.userName}</span>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', flexShrink: 0, boxShadow: '0 0 6px rgba(74,222,128,0.4)' }} />
              </div>
            ))}
          </div>
        )}
        <button
          onClick={() => { setShowPlayers(!showPlayers); setShowShapes(false); setShowLights(false); }}
          onMouseEnter={() => setTooltip(`${onlinePlayers.length} Online`)}
          onMouseLeave={() => setTooltip(null)}
          style={btnStyle(false)}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {/* Stacked avatars (max 3) */}
            {onlinePlayers.slice(0, 3).map((p, i) => (
              <div key={p.id} style={{
                width: 20, height: 20, borderRadius: '50%', background: nameColor(p.userName),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 8, fontWeight: 700, color: '#fff',
                border: '1.5px solid rgba(30,30,40,0.9)',
                marginLeft: i > 0 ? -6 : 0, zIndex: 3 - i,
              }}>{getInitials(p.userName)}</div>
            ))}
            {onlinePlayers.length > 3 && (
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginLeft: 2 }}>+{onlinePlayers.length - 3}</span>
            )}
            {onlinePlayers.length === 0 && <span style={{ fontSize: 12 }}>0</span>}
          </span>
        </button>
      </div>

      {showBuildTools && (
        <>
          <Divider />

          {/* Add Primitive */}
          <div style={{ position: 'relative' }}>
            {showShapes && (
              <div style={dropdownStyle}>
                {SHAPES.map(s => (
                  <button key={s.value} onClick={() => startPlaceShape(s.value)} style={dropdownItemStyle}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >{s.label}</button>
                ))}
              </div>
            )}
            <ToolBtn icon="+" label="Add Shape" active={buildTool === 'place'} tooltip="Add Primitive Shape — click the ground to place, R rotates, Esc exits"
              onClick={() => { setShowShapes(!showShapes); setShowLights(false); setShowPlayers(false); }}
              setTooltip={setTooltip} />
          </div>

          {/* Add Light */}
          <div style={{ position: 'relative' }}>
            {showLights && (
              <div style={dropdownStyle}>
                {LIGHTS.map(l => (
                  <button key={l.value} onClick={() => addLight(l.value)} style={dropdownItemStyle}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >{l.label}</button>
                ))}
              </div>
            )}
            <ToolBtn icon="*" label="Light" active={false} tooltip="Add Light Source"
              onClick={() => { setShowLights(!showLights); setShowShapes(false); setShowPlayers(false); }}
              setTooltip={setTooltip} />
          </div>

          <Divider />

          {/* Transform tools */}
          {TOOLS.map(t => (
            <ToolBtn key={t.value} icon={t.icon} label="" active={activeTool === t.value} tooltip={t.label}
              onClick={() => {
                closeDropdowns();
                if (t.value === 'delete') handleDelete();
                else setActiveTool(t.value);
              }}
              setTooltip={setTooltip} />
          ))}

          <Divider />

          {/* Duplicate */}
          <ToolBtn icon="⧉" label="" active={false} tooltip="Duplicate Selected"
            disabled={!selectedObjectId}
            onClick={() => { closeDropdowns(); handleDuplicate(); }}
            setTooltip={setTooltip} />

          {/* Undo / Redo */}
          <ToolBtn icon="↩" label="" active={false} tooltip="Undo (Ctrl+Z)"
            onClick={() => { closeDropdowns(); undoWorld(worldId); }}
            setTooltip={setTooltip} />
          <ToolBtn icon="↪" label="" active={false} tooltip="Redo (Ctrl+Shift+Z)"
            onClick={() => { closeDropdowns(); redoWorld(worldId); }}
            setTooltip={setTooltip} />

          <Divider />

          {/* Snap: one magnet for grid + rotation snapping, plus a size cycle */}
          <ToolBtn icon={<Magnet size={15} />} label="" active={snapSettings.grid}
            tooltip={snapSettings.grid ? `Snap ON (${snapSettings.gridSize}u / 15°)` : 'Snap OFF'}
            onClick={() => {
              closeDropdowns();
              const on = !snapSettings.grid;
              setSnapSettings({ grid: on, rotation: on });
            }}
            setTooltip={setTooltip} />
          <ToolBtn icon={`${snapSettings.gridSize}`} label="" active={false}
            tooltip="Snap size — click to cycle 0.25 / 0.5 / 1"
            onClick={() => { closeDropdowns(); cycleSnapSize(); }}
            setTooltip={setTooltip} />

          <Divider />

          {/* Build Tools — mutually exclusive */}
          <ToolBtn icon={<Grid3x3 size={15} />} label="" active={topDownView}
            tooltip="Top-down build view (T)"
            onClick={() => { closeDropdowns(); setTopDownView(!topDownView); }}
            setTooltip={setTooltip} />
          <ToolBtn icon={<BoxSelect size={15} />} label="" active={buildTool === 'select'}
            tooltip="Marquee select — drag a box to select objects (best in top-down), drag inside it to move them, G groups, Esc clears"
            onClick={() => { closeDropdowns(); toggleBuildTool('select'); }}
            setTooltip={setTooltip} />
          <ToolBtn icon={<BoxIcon size={15} />} label="" active={buildTool === 'box'}
            tooltip="Box tool — drag a footprint on the grid, then pull it up to set the height"
            onClick={() => { closeDropdowns(); toggleBuildTool('box'); }}
            setTooltip={setTooltip} />
          <ToolBtn icon={<Trees size={15} />} label="" active={buildTool === 'foliage'}
            tooltip="Foliage brush — drag on the ground to paint plants and trees. Strokes are saved, not the plants, so density and species stay editable"
            onClick={() => { closeDropdowns(); toggleBuildTool('foliage'); }}
            setTooltip={setTooltip} />
          <ToolBtn icon={<BrickWall size={15} />} label="" active={buildTool === 'wall'}
            tooltip="Wall tool — drag on the ground to draw, chains from the last endpoint, Esc exits"
            onClick={() => { closeDropdowns(); toggleBuildTool('wall'); }}
            setTooltip={setTooltip} />
          <ToolBtn icon={<Square size={15} />} label="" active={buildTool === 'room'}
            tooltip="Room tool — drag a rectangle to get 4 walls + a floor, Esc exits"
            onClick={() => { closeDropdowns(); toggleBuildTool('room'); }}
            setTooltip={setTooltip} />
          <ToolBtn icon={<DoorOpen size={15} />} label="" active={buildTool === 'door'}
            tooltip="Doorway tool — hover a wall, click to cut a 1×2.1m doorway, Esc exits"
            onClick={() => { closeDropdowns(); toggleBuildTool('door'); }}
            setTooltip={setTooltip} />
          <ToolBtn icon={<PanelTop size={15} />} label="" active={buildTool === 'window'}
            tooltip="Window tool — hover a wall, click to cut a 1.2×1.2m window, Esc exits"
            onClick={() => { closeDropdowns(); toggleBuildTool('window'); }}
            setTooltip={setTooltip} />
          <ToolBtn icon={<Landmark size={15} />} label="" active={buildTool === 'arch'}
            tooltip="Arch tool — hover a wall, click to cut an arched doorway (1.1m wide)"
            onClick={() => { closeDropdowns(); toggleBuildTool('arch'); }}
            setTooltip={setTooltip} />
          <ToolBtn icon={<Warehouse size={15} />} label="" active={buildTool === 'arch-wide'}
            tooltip="Wide arch tool — hover a wall, click to cut a wide arched opening (2.4m)"
            onClick={() => { closeDropdowns(); toggleBuildTool('arch-wide'); }}
            setTooltip={setTooltip} />
          <ToolBtn icon={<TrendingUp size={15} />} label="" active={buildTool === 'stairs'}
            tooltip="Stairs tool — drag on the ground to build a rising run of steps, Esc exits"
            onClick={() => { closeDropdowns(); toggleBuildTool('stairs'); }}
            setTooltip={setTooltip} />
          <div style={{ position: 'relative' }}>
            {buildTool === 'paint' && (
              <div style={{ ...dropdownStyle, flexDirection: 'row', flexWrap: 'wrap', minWidth: 150, maxWidth: 150, gap: 4, padding: 6 }}>
                {PAINT_SWATCHES.map((c) => (
                  <button
                    key={c}
                    onClick={() => setPaintColor(c)}
                    onMouseEnter={() => setTooltip(`Paint ${c}`)}
                    onMouseLeave={() => setTooltip(null)}
                    style={{
                      width: 22, height: 22, borderRadius: 6, background: c, cursor: 'pointer',
                      border: paintColor === c ? '2px solid #00c2b3' : '1px solid rgba(255,255,255,0.25)',
                      boxShadow: paintColor === c ? '0 0 8px rgba(0,194,179,0.6)' : 'none',
                      padding: 0,
                    }}
                  />
                ))}
              </div>
            )}
            <ToolBtn icon={<Paintbrush size={15} />} label="" active={buildTool === 'paint'}
              tooltip="Paint tool — click a shape to recolor it, Esc exits"
              onClick={() => { closeDropdowns(); toggleBuildTool('paint'); }}
              setTooltip={setTooltip} />
          </div>

        </>
      )}

      <Divider />

      {/* Capture — screenshot + recording, always visible */}
      <ToolBtn icon={<Camera size={15} />} label="" active={false}
        tooltip="Screenshot — save this view as a PNG"
        onClick={() => { closeDropdowns(); takeScreenshot(); }}
        setTooltip={setTooltip} />
      <ToolBtn icon={<Video size={15} color={recording ? '#ff4d4d' : undefined} />} label="" active={recording}
        tooltip={recording ? 'Stop and save recording' : 'Record a video of the build view'}
        onClick={() => { closeDropdowns(); toggleRecord(); }}
        setTooltip={setTooltip} />

      {/* Panel background toggle — always visible */}
      <ToolBtn icon="◐" label="" active={panelsSolid} tooltip={panelsSolid ? 'Glass Panels' : 'Solid Panels'}
        onClick={() => { closeDropdowns(); togglePanelOpacity(); }}
        setTooltip={setTooltip} />
      {finished && <RecordingSavedDialog rec={finished} onClose={() => setFinished(null)} />}
    </div>
  );
};

// --- Sub-components ---

const Divider = () => (
  <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.12)', margin: '0 2px', flexShrink: 0 }} />
);

const ToolBtn: React.FC<{
  icon: React.ReactNode; label: string; active: boolean; tooltip: string;
  onClick: () => void; setTooltip: (t: string | null) => void; disabled?: boolean;
}> = ({ icon, label, active, tooltip, onClick, setTooltip, disabled }) => (
  <button
    onClick={onClick}
    aria-label={tooltip}
    onMouseEnter={() => setTooltip(tooltip)}
    onMouseLeave={() => setTooltip(null)}
    disabled={disabled}
    style={{
      ...btnStyle(active),
      opacity: disabled ? 0.4 : 1,
      cursor: disabled ? 'default' : 'pointer',
    }}
  >
    {icon}{label ? ` ${label}` : ''}
  </button>
);

// --- Styles ---

const barStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 12,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: 3,
  alignItems: 'center',
  zIndex: 1000,
  pointerEvents: 'auto',
  padding: '5px 8px',
  background: 'rgba(5, 14, 24, 0.88)',
  backdropFilter: 'blur(20px)',
  borderRadius: 16,
  border: '1px solid rgba(0, 194, 179, 0.15)',
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.5)',
};

const btnStyle = (active: boolean): React.CSSProperties => ({
  background: active ? 'rgba(0,194,179,0.8)' : 'transparent',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '6px 10px',
  fontSize: 14,
  cursor: 'pointer',
  transition: 'all 0.15s',
  minWidth: 32,
  textAlign: 'center' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
});

const tooltipStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '100%',
  left: '50%',
  transform: 'translateX(-50%)',
  marginBottom: 8,
  padding: '5px 10px',
  background: 'rgba(0,0,0,0.9)',
  color: '#fff',
  fontSize: 11,
  borderRadius: 6,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '100%',
  left: '50%',
  transform: 'translateX(-50%)',
  marginBottom: 8,
  background: 'rgba(15,15,25,0.95)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 10,
  padding: 4,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  backdropFilter: 'blur(12px)',
  minWidth: 110,
};

const dropdownItemStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '7px 12px',
  fontSize: 13,
  cursor: 'pointer',
  textAlign: 'left',
};

export default BuilderToolbar;
